// src/pages/MobileAuthTest.tsx
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { mobileAuthDebugger } from '@/utils/mobileAuthDebugger';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
// import { remoteLogger } from '@/utils/remoteLogger';

export default function MobileAuthTest() {
  // Only allow access in development mode
  if (import.meta.env.PROD) {
    return <Navigate to="/" />;
  }
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [session, setSession] = useState<any>(null);
  const [storageData, setStorageData] = useState<any>({});
  const { user, userRole, ready } = useAuth();
  
  useEffect(() => {
    // Collect initial debug info
    const info = mobileAuthDebugger.collectDebugInfo({
      isReady: ready,
      user,
      userRole
    });
    setDebugInfo(info);
    
    // Check for common issues
    const detectedIssues = mobileAuthDebugger.diagnoseCommonIssues();
    setIssues(detectedIssues);
    
    // Check session
    checkSession();
    
    // Check storage
    checkStorage();
  }, [user, userRole, ready]);
  
  const checkSession = async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
  };
  
  const checkStorage = () => {
    const data: any = {};
    
    // Check localStorage
    try {
      const supabaseKey = 'sb-muoihzfomfercsujyyta-auth-token';
      const stored = localStorage.getItem(supabaseKey);
      data.localStorage = stored ? JSON.parse(stored) : null;
    } catch (e) {
      data.localStorageError = e.message;
    }
    
    // Check cookies
    data.cookies = document.cookie;
    
    setStorageData(data);
  };
  
  const testAuth = async () => {
    mobileAuthDebugger.logAuthEvent('Manual Auth Test Started');
    
    try {
      // Test getting session
      const { data, error } = await supabase.auth.getSession();
      
      mobileAuthDebugger.logAuthEvent('Manual Auth Test Result', {
        hasSession: !!data.session,
        hasError: !!error,
        error: error?.message
      });
      
      if (data.session) {
        // Test getting user profile
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', data.session.user.id)
          .single();
          
        mobileAuthDebugger.logAuthEvent('Profile Fetch Test', {
          hasProfile: !!profile,
          hasError: !!profileError,
          error: profileError?.message,
          profile
        });
      }
    } catch (e) {
      mobileAuthDebugger.logAuthEvent('Auth Test Error', { error: e.message });
    }
  };
  
  const clearAuth = async () => {
    await supabase.auth.signOut();
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };
  
  const sendTestLog = async () => {
    try {
      console.log('Attempting to send test log...');
      
      // Test direct fetch to see what happens
      const testResponse = await fetch('https://muoihzfomfercsujyyta.supabase.co/functions/v1/log-sink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          level: 'INFO',
          message: 'Direct test from mobile test page',
          context: { source: 'mobile-test-page' }
        })
      });
      
      console.log('Direct fetch response:', testResponse.status, await testResponse.text());
      
      // Also try via remote logger
      // remoteLogger.info('Mobile Auth Test - Manual Log', {
      //   component: 'MobileAuthTest',
      //   debugInfo,
      //   issues,
      //   timestamp: new Date().toISOString()
      // });
      
      alert('Test log sent! Check console and database for results.');
    } catch (error) {
      console.error('Error sending test log:', error);
      alert('Error sending test log: ' + error.message);
    }
  };
  
  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Mobile Auth Diagnostic Tool</h1>
      
      {/* Issues Alert */}
      {issues.length > 0 && (
        <Card className="p-4 mb-6 bg-red-50 border-red-200">
          <h2 className="font-semibold text-red-800 mb-2">Detected Issues:</h2>
          <ul className="list-disc list-inside">
            {issues.map((issue, i) => (
              <li key={i} className="text-red-700">{issue}</li>
            ))}
          </ul>
        </Card>
      )}
      
      {/* Auth State */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-2">Auth State:</h2>
        <div className="space-y-1 text-sm">
          <p>Ready: {ready ? 'Yes' : 'No'}</p>
          <p>User: {user?.email || 'None'}</p>
          <p>Role: {userRole || 'None'}</p>
          <p>User ID: {user?.id || 'None'}</p>
        </div>
      </Card>
      
      {/* Session Info */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-2">Session Info:</h2>
        <div className="space-y-1 text-sm">
          <p>Has Session: {session ? 'Yes' : 'No'}</p>
          <p>Session User: {session?.user?.email || 'None'}</p>
          <p>Session Role: {session?.user?.user_metadata?.role || 'None'}</p>
        </div>
      </Card>
      
      {/* Device Info */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-2">Device Info:</h2>
        <div className="space-y-1 text-sm">
          <p>Platform: {debugInfo?.platform}</p>
          <p>User Agent: {debugInfo?.userAgent}</p>
          <p>Screen: {debugInfo?.screenWidth}x{debugInfo?.screenHeight}</p>
          <p>Viewport: {debugInfo?.viewportWidth}x{debugInfo?.viewportHeight}</p>
          <p>Online: {debugInfo?.onLine ? 'Yes' : 'No'}</p>
          <p>Connection: {debugInfo?.effectiveType || 'Unknown'}</p>
        </div>
      </Card>
      
      {/* Storage Capabilities */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-2">Storage Capabilities:</h2>
        <div className="space-y-1 text-sm">
          <p>Cookies: {debugInfo?.cookiesEnabled ? '✓' : '✗'}</p>
          <p>LocalStorage: {debugInfo?.localStorageAvailable ? '✓' : '✗'}</p>
          <p>SessionStorage: {debugInfo?.sessionStorageAvailable ? '✓' : '✗'}</p>
          <p>IndexedDB: {debugInfo?.indexedDBAvailable ? '✓' : '✗'}</p>
        </div>
      </Card>
      
      {/* Storage Data */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-2">Storage Data:</h2>
        <div className="text-xs font-mono overflow-auto">
          <p>LocalStorage: {storageData.localStorage ? 'Has auth data' : 'No auth data'}</p>
          <p>Cookies: {storageData.cookies || 'None'}</p>
          {storageData.localStorageError && (
            <p className="text-red-600">Error: {storageData.localStorageError}</p>
          )}
        </div>
      </Card>
      
      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={testAuth}>Test Auth Flow</Button>
        <Button onClick={sendTestLog} variant="secondary">Send Debug Log</Button>
        <Button onClick={clearAuth} variant="destructive">Clear Auth Data</Button>
        <Button onClick={() => window.location.reload()} variant="outline">Refresh Page</Button>
      </div>
      
      {/* Raw Debug Info */}
      <details className="mt-6">
        <summary className="cursor-pointer font-semibold">Raw Debug Info</summary>
        <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto">
          {JSON.stringify(debugInfo, null, 2)}
        </pre>
      </details>
    </div>
  );
}